import { useEffect, useState } from 'react'
import type { Action, ActiveAction } from '../../domain/action'
import { actionStatsLabel, rewardFor } from '../../domain/action'
import type { Area } from '../../domain/area'
import { AddIcon, CheckCircleIcon, CloseIcon, CounterIcon, MinusIcon, TimerIcon, TrophyIcon } from './Icons'
import { useI18n } from '../../i18n'

interface ActiveActionCardProps {
  action: Action
  area: Area
  active: ActiveAction
  saving: boolean
  specializationLabel: string
  onAmount: (amount: number) => void
  onComplete: () => Promise<void>
  onAbandon: () => Promise<void>
}

const ActionIcon = ({ type }: { type: Action['type'] }) => type === 'timer' ? <TimerIcon /> : type === 'counter' ? <CounterIcon /> : <CheckCircleIcon />

export const ActiveActionCard = ({ action, area, active, saving, specializationLabel, onAmount, onComplete, onAbandon }: ActiveActionCardProps) => {
  const { language, locale, copy: { common, action: actionCopy, active: activeCopy } } = useI18n()
  const [now, setNow] = useState(Date.now())
  const [confirmUnique, setConfirmUnique] = useState(false)
  const reward = rewardFor(action, active.amount)
  const elapsedMinutes = Math.floor(Math.max(0, now - active.startedAt) / 60_000)
  const startedAt = new Date(active.startedAt).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
  const presets = action.type === 'timer' ? [5, 10, 15, 30, 45, 60] : [5, 10, 25, 50, 100]

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(interval)
  }, [])

  return (
    <section className="active-action-card" style={{ '--action-color': area.color } as React.CSSProperties}>
      <div className="active-action-card__heading">
        <span className="active-action-card__icon"><ActionIcon type={action.type} /></span>
        <div className="active-action-card__identity">
          <span>{actionCopy.active}</span>
          <h2>{action.name}</h2>
          {action.repeatMode === 'once' && <strong><TrophyIcon /> {actionCopy.once}</strong>}
          <p>{area.name} · {specializationLabel || common.noSkill} · {actionStatsLabel(action, language)}</p>
          <small>{activeCopy.started} {startedAt} · {activeCopy.elapsed} {elapsedMinutes} min</small>
        </div>
        <button className="icon-button" type="button" disabled={saving} onClick={() => void onAbandon()} aria-label={activeCopy.remove}><CloseIcon /></button>
      </div>

      {action.type !== 'simple' && (
        <div className="amount-control">
          <p>{action.type === 'timer' ? activeCopy.minutes : activeCopy.repetitions}</p>
          <div className="preset-list">
            {presets.map((preset) => <button key={preset} type="button" aria-pressed={active.amount === preset} className={active.amount === preset ? 'preset preset--selected' : 'preset'} onClick={() => onAmount(preset)}>{action.type === 'timer' ? `${preset} min` : preset}</button>)}
          </div>
          <div className="stepper">
            <button className="stepper__button" type="button" disabled={active.amount <= 1} onClick={() => onAmount(active.amount - 1)} aria-label={activeCopy.subtract}><MinusIcon /></button>
            <strong>{active.amount}</strong>
            <button className="stepper__button" type="button" onClick={() => onAmount(active.amount + 1)} aria-label={activeCopy.add}><AddIcon /></button>
          </div>
        </div>
      )}

      {confirmUnique ? (
        <div className="unique-confirm">
          <p>“{action.name}” {activeCopy.uniqueReward} +{reward.xp} XP + {reward.coins} 🪙. {activeCopy.uniqueAfter}</p>
          <div><button className="button button--ghost" type="button" onClick={() => setConfirmUnique(false)}>{activeCopy.notYet}</button><button className="button button--complete" type="button" disabled={saving} onClick={() => void onComplete()}>{activeCopy.complete}</button></div>
        </div>
      ) : (
        <button className="button button--complete active-action-card__complete" type="button" disabled={saving} onClick={() => action.repeatMode === 'once' ? setConfirmUnique(true) : void onComplete()}>
          <CheckCircleIcon />{action.type === 'timer' ? activeCopy.log : activeCopy.complete} · +{reward.xp} XP · +{reward.coins} 🪙
        </button>
      )}
    </section>
  )
}
